<div data-test="plan-usage-page" [class]="floatingPageClasses()">
@if (isLoading()) {
  <lib-overlay-loader />
} @else if (isLoaded()) {
  <h5 class="text-neutral-900 font-semibold text-2xl mb-sm">{{ 'plan_usage_screen_title' | transloco }}</h5>
  <p class="text-neutral-500 mb-2xl">{{ 'plan_usage_screen_desc' | transloco }}</p>

  @if (subscription(); as sub) {
    <section class="bg-white border border-neutral-200 rounded-lg p-lg mb-2xl" [class.floating-card]="floating()">
      <div class="flex justify-between items-center gap-lg mb-lg">
        <div>
          <p class="view-mode-label">{{ 'plan_usage_current_plan_label' | transloco }}</p>
          <p class="text-neutral-900 font-semibold text-xl">{{ sub.plan?.name ?? ('plan_usage_plan_unavailable' | transloco) }}</p>
        </div>
        @if (floating()) {
          <span data-test="plan-status" class="rounded-full px-md py-x-sm text-sm font-medium text-white"
            [class]="statusBadgeClass()">
            {{ sub.status.label }}
          </span>
        } @else {
          <span class="rounded-full px-md py-xs text-sm"
            [class]="sub.status.value === subscriptionStatusEnum.ACTIVE ? 'bg-primary-50 text-primary-700' : 'bg-neutral-50 text-neutral-700'">
            {{ sub.status.label }}
          </span>
        }
      </div>

      @if (sub.status.value === subscriptionStatusEnum.PENDING_PAYMENT) {
        <p class="text-neutral-500 mb-lg">{{ 'plan_usage_pending_hint' | transloco }}</p>
      }

      @if (sub.not_renewable) {
        <div data-test="not-renewable" class="border border-dashed border-neutral-200 rounded-lg p-md mb-lg">
          <p class="text-neutral-900 font-medium">{{ 'plan_usage_not_renewable_title' | transloco }}</p>
          <p class="text-neutral-500 text-sm">{{ 'plan_usage_not_renewable_desc' | transloco }}</p>
        </div>
      }

      <div class="grid grid-cols-2 md:grid-cols-5 gap-lg">
        <div>
          <p class="view-mode-label">{{ 'plan_usage_billing_period_label' | transloco }}</p>
          <p class="view-mode-value text-neutral-900">{{ sub.price.billing_period.label }}</p>
        </div>
        <div>
          <p class="view-mode-label">{{ 'plan_usage_price_label' | transloco }}</p>
          <p class="view-mode-value text-neutral-900">{{ sub.price.currency }} {{ sub.price.amount }}</p>
        </div>
        <div>
          <p class="view-mode-label">{{ 'plan_usage_starts_label' | transloco }}</p>
          <p class="view-mode-value text-neutral-900">{{ sub.starts_at | date: 'dd-MMM-yyyy' : 'UTC' }}</p>
        </div>
        <div>
          <p class="view-mode-label">{{ 'plan_usage_ends_label' | transloco }}</p>
          <p class="view-mode-value text-neutral-900">{{ sub.ends_at | date: 'dd-MMM-yyyy' : 'UTC' }}</p>
        </div>
        <div>
          <p class="view-mode-label">{{ 'plan_usage_auto_renew_label' | transloco }}</p>
          <p class="view-mode-value text-neutral-900">
            {{ (sub.auto_renew ? 'plan_usage_auto_renew_on' : 'plan_usage_auto_renew_off') | transloco }}
          </p>
        </div>
      </div>
    </section>
  } @else {
    <section data-test="no-plan" class="bg-white border border-neutral-200 rounded-lg p-lg mb-2xl" [class.floating-card]="floating()">
      <p class="text-neutral-900 font-semibold">{{ 'plan_usage_empty_title' | transloco }}</p>
      <p class="text-neutral-500">{{ 'plan_usage_empty_desc' | transloco }}</p>
    </section>
  }

  @if (subscription()?.live_successor; as upcoming) {
    <section data-test="upcoming" class="border border-dashed border-neutral-200 p-lg mb-2xl" [class]="floating() ? 'bg-white rounded-xl' : 'bg-neutral-50 rounded-lg'">
      <p class="text-neutral-900 font-semibold text-lg mb-sm">{{ 'plan_usage_upcoming_title' | transloco }}</p>
      <p class="text-neutral-500">
        {{ 'plan_usage_upcoming_moving_to' | transloco }}
        <span class="text-neutral-900 font-medium">{{ upcoming.plan?.name ?? ('plan_usage_plan_unavailable' | transloco) }}</span>
        {{ 'plan_usage_upcoming_on' | transloco }}
        <span class="text-neutral-900 font-medium">{{ upcoming.starts_at | date: 'dd-MMM-yyyy' : 'UTC' }}</span>
      </p>
      @if (upcoming.awaiting_payment) {
        <p data-test="upcoming-awaiting-payment" class="text-neutral-500">{{ 'plan_usage_upcoming_awaiting_payment' | transloco }}</p>
      }
    </section>
  }

  @if (limitRows(); as limits) {
    <section data-test="limits" class="bg-white border border-neutral-200 rounded-lg p-lg" [class.floating-card]="floating()">
      <p class="text-neutral-900 font-semibold text-lg mb-lg">{{ 'plan_usage_limits_title' | transloco }}</p>
      @if (floating()) {
        <!-- Floating: one tile per limit with a full-width usage bar -->
        <div class="grid gap-lg [grid-template-columns:repeat(auto-fill,minmax(18rem,1fr))]">
          @for (row of limits; track row.key) {
            @let nearLimit = !row.atLimit && row.ratio >= 0.8;
            <div data-test="limit-tile" class="flex flex-col gap-md rounded-lg border p-lg"
              [class]="row.atLimit ? 'border-destructive-200 bg-destructive-50' : 'border-neutral-200 bg-white'">
              <div class="flex items-center justify-between gap-md">
                <p class="text-sm font-medium text-neutral-700">{{ row.name }}</p>
                @if (row.limit === null) {
                  <span class="rounded-full bg-neutral-100 px-sm py-x-sm text-xs font-medium text-neutral-700">
                    {{ 'plan_usage_unlimited' | transloco }}
                  </span>
                } @else if (row.atLimit) {
                  <span data-test="at-limit" class="rounded-full bg-destructive-500 px-sm py-x-sm text-xs font-medium text-white">
                    {{ 'plan_usage_screen_limit_exceeded_label' | transloco }}
                  </span>
                } @else {
                  <span class="text-xs font-medium" [class]="nearLimit ? 'text-warning-700' : 'text-neutral-500'">
                    {{ row.ratio * 100 | number: '1.0-0' }}%
                  </span>
                }
              </div>

              <p class="flex items-baseline gap-xs">
                <span class="text-2xl font-bold" [class]="row.atLimit ? 'text-destructive-700' : 'text-neutral-900'">
                  {{ row.usage | number }}
                </span>
                @if (row.limit !== null) {
                  <span class="text-sm text-neutral-500">/ {{ row.limit | number }}</span>
                }
              </p>

              @if (row.limit !== null) {
                <div class="h-2 w-full rounded-full bg-neutral-100 overflow-hidden" aria-hidden="true">
                  <div data-test="limit-bar" class="h-full rounded-full"
                    [class]="row.atLimit ? 'bg-destructive-500' : nearLimit ? 'bg-warning-500' : 'bg-primary-500'"
                    [style.width.%]="row.ratio * 100"></div>
                </div>
              }
            </div>
          }
        </div>
      } @else {
      @for (row of limits; track row.key) {
        <div data-test="limit-row" class="flex flex-wrap justify-between items-center gap-lg py-md border-t border-neutral-200 first:border-t-0">
          <div>
            <p class="text-neutral-900 font-medium">{{ row.name }}</p>
          </div>
          <div class="flex items-center gap-md">
            @if (row.limit !== null) {
              <div class="h-2 w-40 rounded-full bg-neutral-50 overflow-hidden" aria-hidden="true">
                <div class="h-full rounded-full" [class]="row.atLimit ? 'bg-destructive-500' : 'bg-primary-500'"
                  [style.width.%]="row.ratio * 100"></div>
              </div>
            }
            <span [class]="row.atLimit ? 'text-destructive-500' : 'text-neutral-900'">
              @if (row.atLimit) { <span data-test="at-limit" class="sr-only">!</span> }
              {{ row.usage | number }}
              @if (row.limit === null) {
                <span class="text-neutral-500">· {{ 'plan_usage_unlimited' | transloco }}</span>
              } @else {
                <span class="text-neutral-500">{{ 'plan_usage_of' | transloco }} {{ row.limit | number }}</span>
              }
            </span>
          </div>
        </div>
      }
      }
    </section>
  }
}
</div>
